/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';

import type { AIMessageDraft } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { mockCompileEmailHtml } from 'bundles/enterprise-admin-messaging-v2/components/common/mockCompileEmailHtml';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

// TODO: source From/To from the org profile + the resolved learner list.
// Stub strings are inlined at the call site so _t() is invoked at render
// time (per Coursera translation standards).

type Props = {
  draft: AIMessageDraft;
  /** Send action. Omit for a read-only preview (no actions bar). */
  onSend?: () => void;
  /** Back action. Omit for a read-only preview (no actions bar). */
  onBack?: () => void;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  metadata: css`
    display: grid;
    grid-template-columns: max-content 1fr;
    column-gap: var(--cds-spacing-150);
    row-gap: var(--cds-spacing-50);
  `,
  metadataLabel: css`
    color: var(--cds-color-neutral-primary);
  `,
  metadataValue: css`
    color: var(--cds-color-neutral-primary-weak);
    word-break: break-word;
  `,
  /* The iframe sits inside this card; the card supplies the rounded outer
     frame and the iframe paints the email body. Height matches the design
     mock — the iframe scrolls internally if the body overflows. */
  emailFrame: css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-100);
    overflow: hidden;
    background: var(--cds-color-neutral-background-primary-weak);
  `,
  iframe: css`
    display: block;
    width: 100%;
    height: 480px;
    border: 0;
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
    width: 100%;
  `,
};

/**
 * Preview-and-send sub-screen. Renders the compiled email HTML inside a
 * sandboxed iframe so the email's inline styles can't leak into the app
 * (and vice versa). Send transitions to the Sent confirmation; Back
 * returns to Review with the draft preserved. With neither handler the
 * preview is read-only (no actions bar) — e.g. the "See preview" link on the
 * resend success toast.
 */
const PreviewEmailScreen = ({ draft, onSend, onBack }: Props) => {
  const compiledHtml = React.useMemo(() => mockCompileEmailHtml(draft), [draft]);

  return (
    <React.Fragment>
      <Dialog.Content>
        <div css={styles.body}>
          <div css={styles.metadata}>
            <Typography2 component="span" variant="subtitleSmall" css={styles.metadataLabel}>
              {_t('From')}
            </Typography2>
            <Typography2 component="span" variant="bodySecondary" css={styles.metadataValue}>
              {_t('Your Organization <noreply@example.com>')}
            </Typography2>
            <Typography2 component="span" variant="subtitleSmall" css={styles.metadataLabel}>
              {_t('To')}
            </Typography2>
            <Typography2 component="span" variant="bodySecondary" css={styles.metadataValue}>
              {_t('learner@example.com')}
            </Typography2>
            <Typography2 component="span" variant="subtitleSmall" css={styles.metadataLabel}>
              {_t('Subject')}
            </Typography2>
            <Typography2
              component="span"
              variant="bodySecondary"
              css={styles.metadataValue}
              data-testid="ai-message-modal-preview-subject"
            >
              {draft.subject}
            </Typography2>
          </div>
          <div css={styles.emailFrame}>
            <iframe
              title={_t('Email preview')}
              srcDoc={compiledHtml}
              sandbox=""
              referrerPolicy="no-referrer"
              css={styles.iframe}
              data-testid="ai-message-modal-preview-iframe"
            />
          </div>
        </div>
      </Dialog.Content>
      {onSend || onBack ? (
        <Dialog.Actions>
          <div css={styles.actions}>
            {onSend ? (
              <Button variant="primary" onClick={onSend} data-testid="ai-message-modal-preview-send">
                {_t('Send')}
              </Button>
            ) : null}
            {onBack ? (
              <Button variant="ghost" onClick={onBack} data-testid="ai-message-modal-preview-back">
                {_t('Back')}
              </Button>
            ) : null}
          </div>
        </Dialog.Actions>
      ) : null}
    </React.Fragment>
  );
};

export default PreviewEmailScreen;
